import * as React from "react";
import { Drawer, Spinner } from "@blueprintjs/core";
import { css } from "@emotion/core";
import { Outlet, useMatch, useNavigate, useParams } from "react-router-dom";
import { useSinkState, useSinkActions } from "context/sink";
import { OrderMachineContext } from "./state/orderMachine";
import { createOrderMachine } from "./state/orderMachine";
import { GeometryMachineContext } from "./state/geometryMachine";
import {
  LidarMachineContext,
  cleanupLidarWorkerPool,
} from "./state/lidarMachine";
import {
  ImagesMachineContext,
  cleanupImageWorkerPool,
} from "./state/imagesMachine";
import {
  UploadMachineProvider,
  useUploadMachineSelector,
} from "./context/upload-machine";
import { OrderTypeSelect } from "./sections/ChooseOrderType";
import { useFolderOrderTypes } from "hooks";
import { isOrderSubmitted } from "api/orders";

export function generateFileId(file: File): string {
  const [name] = file.name.split(".");
  return `${name}-${file.size}-${file.lastModified}`;
}

function MachineProviders({ children }: { children: React.ReactNode }) {
  const loading = useUploadMachineSelector((state) =>
    state.matches({ uploader: "loading" })
  );

  const imageUploads = useUploadMachineSelector(
    (state) => state.context.imageUploads
  );
  const lidarUploads = useUploadMachineSelector(
    (state) => state.context.lidarUploads
  );
  const geometryUploads = useUploadMachineSelector(
    (state) => state.context.geometryUploads
  );
  const surveyId =
    OrderMachineContext.useSelector((state) => state.context.surveyId) ?? 0;
  const orderId =
    OrderMachineContext.useSelector((state) => state.context.orderId) ?? 0;

  if (loading) return null;

  return (
    <ImagesMachineContext.Provider
      options={{
        input: {
          existingUploads: imageUploads,
          surveyId,
          orderId,
        },
      }}
    >
      <LidarMachineContext.Provider
        options={{
          input: {
            existingUploads: lidarUploads,
            surveyId,
            orderId,
          },
        }}
      >
        <GeometryMachineContext.Provider
          options={{
            input: {
              existingUploads: geometryUploads,
              surveyId,
              orderId,
            },
          }}
        >
          {children}
        </GeometryMachineContext.Provider>
      </LidarMachineContext.Provider>
    </ImagesMachineContext.Provider>
  );
}

function OrderManager({ children }: { children: React.ReactNode }) {
  const match = useMatch("/surveys/:id/orders/*");
  const navigate = useNavigate();

  const surveyId = parseInt(match.params.id);

  const orderId = OrderMachineContext.useSelector(
    (state) => state.context.orderId
  );

  const scheduleAt = OrderMachineContext.useSelector(
    (state) => state.context.scheduleAt
  );

  const orderTypeId = OrderMachineContext.useSelector(
    (state) => state.context.orderTypeId
  );

  const { resink } = useSinkActions();

  const orderType = OrderMachineContext.useSelector(
    (state) => state.context.orderType
  );

  const creating = OrderMachineContext.useSelector((state) =>
    state.matches({ create: "creating" })
  );

  const loading = OrderMachineContext.useSelector((state) =>
    state.matches({ check: "loading" })
  );

  React.useEffect(() => {
    if (!orderId) return;

    // If order is submitted/scheduled, redirect to survey
    if (scheduleAt) {
      navigate(`/surveys/${surveyId}`);
      return;
    }

    // If legacy order (has orderType string but no orderTypeId), redirect to legacy UI
    if (orderType && !orderTypeId) {
      navigate(`/surveys/${surveyId}/order/${orderType}`);
      return;
    }

    // New order - navigate to new UI
    navigate(`/surveys/${surveyId}/orders/${orderId}`);
  }, [navigate, surveyId, orderId, orderTypeId, orderType, scheduleAt]);

  React.useEffect(() => {
    if (orderTypeId) {
      resink();
    }
  }, [orderTypeId, resink]);

  React.useEffect(() => {
    return () => {
      cleanupImageWorkerPool();
      cleanupLidarWorkerPool();
    };
  }, []);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        overflow-y: auto;
      `}
    >
      {loading || creating ? (
        <Spinner />
      ) : orderId ? (
        <UploadMachineProvider
          options={{
            input: {
              surveyId: parseInt(match?.params.id || "0"),
              orderId: orderId,
              resume: scheduleAt ? true : false,
            },
          }}
        >
          <MachineProviders>{children}</MachineProviders>
        </UploadMachineProvider>
      ) : (
        children
      )}
    </div>
  );
}

function OrderHeader({ survey, site }: { survey: any; site: any }) {
  const orderMachine = OrderMachineContext.useActorRef();
  const orderTypeId = OrderMachineContext.useSelector(
    (state) => state.context.orderTypeId
  );
  const orderId = OrderMachineContext.useSelector(
    (state) => state.context.orderId
  );
  const folderId = survey?.folder_id;

  const { selectedSurveyId, sink } = useSinkState();
  const order = sink.orders.find((o) => o.survey_id === selectedSurveyId);
  const isSubmitted = isOrderSubmitted(order);

  const { data: orderTypes, isLoading: isLoadingOrderTypes } =
    useFolderOrderTypes(folderId);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0.5rem;
        `}
      >
        {orderTypeId ? (
          <>
            <span
              css={css`
                font-weight: 400;
                color: var(--neutral-3);
              `}
            >
              Create
            </span>
            {orderId && orderTypes && !isLoadingOrderTypes ? (
              <>
                <OrderTypeSelect
                  orderTypes={orderTypes}
                  selectedOrderType={orderTypeId}
                  disabled={isSubmitted}
                  onOrderTypeSelect={(orderType) => {
                    orderMachine.send({
                      type: "UPDATE_TYPE",
                      orderTypeId: orderType.id,
                    });
                  }}
                />
                <span
                  css={css`
                    font-weight: 400;
                    color: var(--neutral-3);
                  `}
                >
                  order for
                </span>
              </>
            ) : null}
          </>
        ) : null}
      </div>
      <div
        css={css`
          display: flex;
          gap: 0.5rem;
        `}
      >
        <span
          css={css`
            font-weight: 400;
            color: var(--neutral-3);
          `}
        >
          Survey Label:
        </span>
        <span
          css={css`
            font-weight: 700;
          `}
        >
          {survey?.label}
        </span>
      </div>
      {site?.label && (
        <div
          css={css`
            display: flex;
            gap: 0.5rem;
          `}
        >
          <span
            css={css`
              font-weight: 400;
              color: var(--neutral-3);
            `}
          >
            Site:
          </span>
          <span
            css={css`
              font-weight: 700;
            `}
          >
            {site?.label}
          </span>
        </div>
      )}
    </div>
  );
}

export default function CreateOrder() {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const { surveys, selectedSurveyId, sink } = useSinkState();

  const survey = surveys.find((survey) => survey.id === selectedSurveyId);
  const site = (sink as any).sites?.find(
    (site: any) => site.id === (survey as any)?.site_id
  );

  const logic = React.useMemo(
    () =>
      createOrderMachine.provide({
        actions: {
          navigate: ({ context }) => {
            navigate(`/surveys/${survey?.id}/orders/${context.orderId}`);
          },
        },
      }),
    [survey?.id, navigate]
  );

  if (survey === undefined) {
    return null;
  }

  return (
    <OrderMachineContext.Provider
      logic={logic}
      options={{
        input: { surveyId: survey?.id ?? 0 },
      }}
    >
      <Drawer
        isOpen
        size={`calc(100vh - 66px)`}
        position="bottom"
        className="bp6-dark"
        title={<OrderHeader survey={survey} site={site} />}
        onClose={() => navigate(`/surveys/${survey?.id}`)}
        canOutsideClickClose={true}
        canEscapeKeyClose={true}
        isCloseButtonShown={true}
      >
        <OrderManager>
          <div
            css={css`
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              gap: 1rem;
            `}
          >
            <div
              css={css`
                flex: 1;
                overflow-y: hidden;
              `}
            >
              <Outlet />
            </div>
          </div>
        </OrderManager>
      </Drawer>
    </OrderMachineContext.Provider>
  );
}
